import React, { useState, useRef } from 'react';
import { Box, IconButton, CircularProgress, Typography, Snackbar, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ref, uploadBytes, getDownloadURL, getMetadata } from 'firebase/storage';
import { doc, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { db, storage } from '../services/firebase';
import { useAuth } from '../context/AuthContext';

// Styled komponenter
const ProfileImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '70px',
  height: '70px',
  borderRadius: '4px',
  overflow: 'hidden',
  margin: '0 auto',
  backgroundColor: '#666',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  '&:hover': {
    '& .upload-overlay': {
      opacity: 1,
    }
  }
}));

const ProfileImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const UploadOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.3s',
  color: 'white',
}));

const UploadInput = styled('input')({
  display: 'none',
});

interface ProfileImageUploadProps {
  onImageUploaded?: (imageUrl: string) => void;
}

const ProfileImageUpload: React.FC<ProfileImageUploadProps> = ({ onImageUploaded }) => {
  const { currentUser, userName, profileImageUrl, setProfileImageUrl, refreshProfileImage } = useAuth();
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);

  const handleProfileImageClick = () => {
    if (loading) {
      console.log("Upload allerede i gang - ignorerer klik");
      return;
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentUser) {
      console.log("Ingen fil valgt eller bruger ikke logget ind");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log(`Starter upload af billede: ${file.name}, størrelse: ${file.size} bytes, type: ${file.type}`);
      
      // Debugging Firebase Storage konfiguration
      try {
        console.log("Firebase Storage bucket:", storage.app.options.storageBucket);
        console.log("Firebase app config:", storage.app.options);
      } catch (e) {
        console.error("Kunne ikke hente Firebase Storage konfiguration:", e);
      }

      // Tjek om brugeren har en profil
      const userRef = doc(db, 'users', currentUser.uid);
      console.log(`Tjekker om bruger ${currentUser.uid} har en profil...`);
      const userDoc = await getDoc(userRef).catch(err => {
        console.error("Fejl ved hentning af brugerdokument:", err);
        throw new Error(`Kunne ikke tjekke bruger: ${err.message}`);
      });
      
      if (!userDoc.exists()) {
        console.log(`Bruger ${currentUser.uid} har ingen profil i databasen`);
        throw new Error("Din brugerprofil findes ikke i systemet. Kontakt venligst administrator.");
      }
      console.log("Brugerprofil fundet i databasen");

      // Tjek filstørrelse (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error("Billedet er for stort. Max størrelse er 5MB.");
      }

      try {
        // Upload billede til Firebase Storage
        console.log("Opretter reference til Firebase Storage...");
        console.log("Storage konfiguration:", {
          bucket: storage.app.options.storageBucket,
          appId: storage.app.options.appId,
          authDomain: storage.app.options.authDomain,
          userUid: currentUser.uid
        });
        
        const storageRef = ref(storage, `profile_images/${currentUser.uid}`);
        console.log("Storage reference oprettet:", storageRef.fullPath);
        
        // Test tilgængelighed af Storage
        try {
          console.log("Tester Firebase Storage tilgængelighed...");
          const testRef = ref(storage, '_test_connection_');
          try {
            // Forsøg at uploade en lille test-fil for at tjekke om Storage virker
            const testBlob = new Blob(['test'], { type: 'text/plain' });
            await uploadBytes(testRef, testBlob);
            console.log("Firebase Storage er fuldt tilgængelig og fungerer korrekt");
          } catch (testUploadError: any) {
            // Hvis fejlen er "object not found", er Storage stadig tilgængelig
            if (testUploadError.code === 'storage/object-not-found') {
              console.log("Firebase Storage er tilgængelig (forventet 'not found' fejl)");
            } else {
              // Log den specifikke fejl for diagnostik
              console.error("Firebase Storage test fejlede med kode:", testUploadError.code);
              console.error("Firebase Storage fejlbesked:", testUploadError.message);
              if (testUploadError.code === 'storage/unauthorized') {
                throw new Error("Ingen tilladelse til at uploade. Tjek Firebase Storage regler.");
              } else if (testUploadError.code === 'storage/cancelled') {
                throw new Error("Upload blev afbrudt. Tjek netværksforbindelsen.");
              } else if (testUploadError.code === 'storage/unknown') {
                throw new Error("Firebase Storage er ikke korrekt konfigureret.");
              }
              // Fortsæt alligevel, da det egentlige upload stadig kan lykkes
            }
          }
        } catch (testError) {
          console.error("Firebase Storage tilgængelighedstest fejlede:", testError);
          // Logning, men fortsæt med upload alligevel
        }
        
        console.log(`Uploader billede til Firebase Storage: profile_images/${currentUser.uid}`);
        // Brug Promise.race med en timeout for at undgå at hænge
        console.time('uploadDuration');
        const uploadResult = await Promise.race([
          uploadBytes(storageRef, file),
          new Promise<any>((_, reject) => 
            setTimeout(() => reject(new Error("Upload timeout - Firebase Storage svarede ikke")), 15000)
          )
        ]).catch(err => {
          console.error("Fejl ved upload til Firebase Storage:", err);
          throw err;
        });
        console.timeEnd('uploadDuration');
        
        console.log("Billede uploadet! Metadata:", uploadResult?.metadata || "Ingen metadata");

        // Hent URL til det uploadede billede
        console.log("Henter billedets URL...");
        console.time('getUrlDuration');
        const downloadURL = await Promise.race([
          getDownloadURL(storageRef),
          new Promise<string>((_, reject) => 
            setTimeout(() => reject(new Error("Timeout ved hentning af URL - Firebase Storage svarede ikke")), 10000)
          )
        ]).catch(err => {
          console.error("Fejl ved hentning af download URL:", err);
          throw err;
        });
        console.timeEnd('getUrlDuration');
        
        console.log("Billede URL hentet:", downloadURL.substring(0, 50) + "...");

        // Gem URL i Firestore
        await saveProfileUrlToFirestore(downloadURL);
      } catch (storageError: any) {
        console.error('Firebase Storage fejl:', storageError);
        
        // Log detaljerede fejlinformationer
        if (storageError.code) {
          console.error(`Firebase Storage fejlkode: ${storageError.code}`);
        }
        if (storageError.serverResponse) {
          console.error(`Firebase Storage serverResponse: ${storageError.serverResponse}`);
        }
        
        // Tilføj flere fejloplysninger
        console.error('Firebase Storage fejl trace:', {
          errorName: storageError.name,
          errorStack: storageError.stack,
          errorMessage: storageError.message,
          bucketName: storage.app.options.storageBucket,
          storageInstance: !!storage,
          userUID: currentUser?.uid
        });
        
        // Log netværksdetaljer
        try {
          const online = navigator.onLine;
          console.log(`Netværksstatus - Online: ${online}`);
          console.log(`User Agent: ${navigator.userAgent}`);
        } catch (e) {
          console.error("Kunne ikke logge netværksdetaljer:", e);
        }
        
        // MIDLERTIDIGT DEAKTIVERET fallback for at se den faktiske fejl
        /* Deaktiveret for at se præcis hvad fejlen er
        // Specific error handling based on Firebase Storage error codes
        if (storageError.code === 'storage/unauthorized') {
          setError("Ingen tilladelse til at uploade billeder. Tjek Firebase Storage regler.");
          setOpenSnackbar(true);
          throw storageError;
        } else if (storageError.code === 'storage/canceled') {
          setError("Upload blev afbrudt. Tjek din netværksforbindelse.");
          setOpenSnackbar(true);
          throw storageError;
        } else if (storageError.code === 'storage/unknown' || 
            storageError.message?.includes('firebasestorage.googleapis.com') ||
            storageError.message?.includes('timeout') || 
            storageError.message?.includes('Timeout')) {
          
          console.log("Forsøger fallback til lokal lagring...");
          await saveProfileImageLocally(file);
        } else {
          console.error("Ukendt Firebase Storage fejl, kan ikke bruge fallback");
          throw storageError; // Videresend fejlen så den håndteres i den ydre catch
        }
        */
        
        // Videregiv fejlen direkte for at se hvad der sker
        setError(`Firebase Storage fejl: ${storageError.code || storageError.message}`);
        setOpenSnackbar(true);
        throw storageError;
      }

      console.log("Upload processen er gennemført!");

    } catch (error: any) {
      console.error('Fejl ved upload af profilbillede:', error);
      setError(error.message || 'Der opstod en fejl under upload af billede');
      setOpenSnackbar(true);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Nulstil file input
      }
      setLoading(false);
      console.log("Loading tilstand afsluttet");
    }
  };

  // Hjælpefunktion til at gemme profil URL i Firestore
  const saveProfileUrlToFirestore = async (downloadURL: string) => {
    if (!currentUser) return;
    
    console.log("Tjekker brugerens dokument i Firestore...");
    const userRef = doc(db, 'users', currentUser.uid);
    const userDoc = await getDoc(userRef);

    // Kun opdater hvis brugerdokumentet allerede eksisterer
    if (userDoc.exists()) {
      console.log("Opdaterer eksisterende brugerdokument...");
      await updateDoc(userRef, {
        profileImageUrl: downloadURL
      });
      
      // Opdater AuthContext
      console.log("Opdaterer AuthContext...");
      setProfileImageUrl(downloadURL);
      
      // Kald callback hvis den er tilgængelig
      if (onImageUploaded) {
        onImageUploaded(downloadURL);
      }
    } else {
      console.error("Bruger-profil findes ikke i databasen");
      setError("Din brugerprofil findes ikke i systemet. Kontakt venligst administrator.");
      setOpenSnackbar(true);
      throw new Error("Bruger-profil findes ikke");
    }
  };

  // Midlertidig løsning hvis Firebase Storage ikke er tilgængelig
  const saveProfileImageLocally = async (file: File) => {
    if (!currentUser) return Promise.reject(new Error("Bruger ikke logget ind"));
    
    try {
      console.log("Starter lokal lagring af billede...");
      
      // Tjek om brugeren har en profil 
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef).catch(err => {
        console.error("Fejl ved hentning af brugerdokument i lokal lagring:", err);
        throw new Error(`Kunne ikke tjekke bruger i lokal lagring: ${err.message}`);
      });
        
      if (!userDoc.exists()) {
        console.log("Bruger-profil findes ikke under lokal lagring");
        setError("Din brugerprofil findes ikke i systemet. Kontakt venligst administrator.");
        setOpenSnackbar(true);
        return Promise.reject(new Error("Bruger-profil findes ikke"));
      }
      
      // Brug timeout for at sikre, at vi ikke hænger fast
      return await Promise.race([
        new Promise<void>((resolve, reject) => {
          try {
            const reader = new FileReader();
            
            // Begræns billedstørrelse hvis nødvendigt
            let imageToProcess = file;
            if (file.size > 2 * 1024 * 1024) { // Hvis større end 2MB
              console.log("Billedet er stort - det kan tage lidt tid at behandle");
            }
            
            reader.onload = async (e) => {
              try {
                if (e.target?.result) {
                  const dataUrl = e.target.result as string;
                  
                  // Hvis billedet er for stort til localStorage (typisk 5MB grænse)
                  if (dataUrl.length > 4 * 1024 * 1024) {
                    console.log("Billedet er for stort til localStorage - bruger placeholder");
                    
                    // Gem en placeholder i stedet
                    localStorage.setItem('tempProfileImage', 'placeholder');
                    localStorage.setItem('tempProfileImageUserId', currentUser.uid);
                    
                    // Opdater UI - vi kan ikke gemme billedet, men giver feedback
                    setProfileImageUrl(null);
                    setError("Billedet er for stort til midlertidig lagring. Tjek Firebase Storage indstillinger.");
                    setOpenSnackbar(true);
                    
                    resolve();
                    return;
                  }
                  
                  // Hvis billedet er OK størrelse, gem det
                  console.log(`Gemmer billede lokalt, størrelse: ${Math.round(dataUrl.length/1024)}KB`);
                  try {
                    localStorage.setItem('tempProfileImage', dataUrl);
                    localStorage.setItem('tempProfileImageUserId', currentUser.uid);
                    
                    console.log("Billede gemt lokalt som fallback");
                    
                    // Opdater UI
                    setProfileImageUrl(dataUrl);
                    
                    resolve();
                  } catch (localStorageError) {
                    console.error("Fejl ved gem i localStorage:", localStorageError);
                    reject(new Error("Kunne ikke gemme billedet lokalt: " + localStorageError));
                  }
                } else {
                  reject(new Error("Kunne ikke læse filen - ingen data modtaget"));
                }
              } catch (processError) {
                console.error("Fejl under behandling af billede:", processError);
                reject(processError);
              }
            };
            
            reader.onerror = (evt) => {
              console.error("FileReader fejl:", evt);
              reject(new Error("Fejl ved læsning af fil: " + reader.error));
            };
            
            reader.onabort = () => {
              console.log("FileReader afbrudt");
              reject(new Error("Læsning af fil blev afbrudt"));
            };
            
            console.log("Starter læsning af fil...");
            reader.readAsDataURL(imageToProcess);
            
          } catch (err) {
            console.error("Generel fejl i lokal billedbehandling:", err);
            reject(err);
          }
        }),
        new Promise<void>((_, reject) => 
          setTimeout(() => {
            console.log("Timeout ved lokal behandling af billede");
            reject(new Error("Timeout ved behandling af billede lokalt"))
          }, 10000) // 10 sekunder timeout
        )
      ]);
    } catch (error) {
      console.error("Fejl i saveProfileImageLocally:", error);
      throw error; // Sørg for at fejlen propageres
    }
  };

  // Tjek om der er gemt et midlertidigt billede i localStorage
  React.useEffect(() => {
    if (!profileImageUrl && currentUser) {
      const localImage = localStorage.getItem('tempProfileImage');
      const localImageUserId = localStorage.getItem('tempProfileImageUserId');
      
      // Kun indlæs billedet hvis bruger-ID'et matcher den aktuelle bruger
      if (localImage && localImageUserId === currentUser.uid) {
        setProfileImageUrl(localImage);
      }
    }
  }, [profileImageUrl, setProfileImageUrl, currentUser]);

  // Sikkerhedsfunktion for at nulstille loading state efter max 30 sekunder
  React.useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    
    if (loading) {
      // Sæt en timeout der nulstiller loading state efter 30 sekunder
      timeout = setTimeout(() => {
        console.log("TIMEOUT: Upload tog for lang tid - nulstiller loading state");
        setLoading(false);
        setError("Upload tog for lang tid. Prøv igen eller kontakt support.");
        setOpenSnackbar(true);
      }, 30000); // 30 sekunder timeout
    }
    
    // Clean up timer når loading ændrer sig eller komponenten unmountes
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [loading]);

  // Fejlhåndtering for billedindlæsning
  const handleImageError = () => {
    console.error("Fejl ved indlæsning af profilbillede URL:", profileImageUrl);
    setImageLoadError(true);
    
    // Prøv at opdatere billedet med et frisk URL
    if (currentUser) {
      console.log("Forsøger at opdatere profilbillede URL...");
      
      setLoading(true);
      refreshProfileImage()
        .then(() => {
          console.log("Profilbillede URL opdateret");
          setImageLoadError(false);
        })
        .catch(error => {
          console.error("Kunne ikke opdatere profilbillede URL:", error);
          setError("Profilbilledet kunne ikke indlæses. Prøv at uploade det igen.");
          setOpenSnackbar(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <>
      <ProfileImageContainer onClick={handleProfileImageClick}>
        {loading ? (
          <CircularProgress size={30} sx={{ color: 'white' }} />
        ) : profileImageUrl && !imageLoadError ? (
          <ProfileImage 
            src={profileImageUrl} 
            alt={userName || 'Profilbillede'} 
            onError={handleImageError}
          />
        ) : (
          <Typography variant="body2" color="white" sx={{ fontSize: '1.5rem' }}>
            {userName?.charAt(0)?.toUpperCase() || '?'}
          </Typography>
        )}
        <UploadOverlay className="upload-overlay">
          <Typography variant="body2">
            {loading ? 'Uploader...' : 'Opdater'}
          </Typography>
        </UploadOverlay>
        <UploadInput
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          disabled={loading}
        />
      </ProfileImageContainer>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProfileImageUpload; 